import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, List, ListItem, ListItemIcon, ListItemText, MenuItem, Radio, RadioGroup, Stack,
  TextField, Typography,
} from '@mui/material';
import {
  SchoolOutlined as TrainingIcon,
  CheckCircleOutline as CheckIcon,
  MailOutline as MailIcon,
  DescriptionOutlined as DocIcon,
  BadgeOutlined as BadgeIcon,
} from '@mui/icons-material';
import HiringService, { Candidate, HirePreview, HireResult } from '../../api/services/hiring.service';
import { candidateName, humanKey } from './hiringUI';

// Turning a candidate into a driver.
//
// This is a confirm step that tells you exactly what is about to happen,
// because what happens is not small: an account is created, an invitation
// goes out to a real person, and their paperwork moves into the permanent
// record. "Are you sure?" is not enough information to answer that
// question with.

const ROLES = ['Driver', 'BC', 'Maintainer'];

// A new driver starts in training: every day their trainer is asked whether
// it goes on and how it is going, and payroll pays training days until the
// trainer says it is finished. Someone who can already drive the route
// starts ready, and is paid normally from day one.
type Start = 'training' | 'ready';

const HireDialog: React.FC<{
  open: boolean;
  candidate: Candidate;
  /** The BCs who can train; the candidate's own trainer is picked first. */
  trainers?: Array<{ accountId: string; name: string }>;
  onClose: () => void;
  onHired: (result: HireResult) => void;
}> = ({
  open, candidate, trainers = [], onClose, onHired,
}) => {
  const [preview, setPreview] = useState<HirePreview | null>(null);
  const [role, setRole] = useState('Driver');
  const [employeeId, setEmployeeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [start, setStart] = useState<Start>('training');
  const [trainerId, setTrainerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setStartDate(candidate.targetStartDate ? candidate.targetStartDate.slice(0, 10) : '');
    setStart('training');
    setTrainerId(candidate.trainerAccountId || '');
    HiringService.hirePreview(candidate._id)
      .then(setPreview)
      .catch((err) => setError(err?.response?.data?.message || 'Could not check that candidate.'))
      .finally(() => setLoading(false));
  }, [open, candidate._id, candidate.targetStartDate, candidate.trainerAccountId]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await HiringService.hire(candidate._id, {
        role,
        employeeId: employeeId.trim() || undefined,
        startDate: startDate || undefined,
        ...(role === 'Driver' ? { training: start === 'training', trainerId: start === 'training' && trainerId ? trainerId : undefined } : {}),
      });
      onHired(result);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'The hire did not go through.');
    } finally {
      setSubmitting(false);
    }
  };

  const blockers = preview?.blockers ?? [];
  const driver = role === 'Driver';
  const trainerName = trainers.find((t) => t.accountId === trainerId)?.name;
  const firstDay = startDate ? new Date(`${startDate}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : 'today';
  const canHire = !loading && blockers.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Hire {candidateName(candidate)}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {blockers.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700} gutterBottom>
              Not yet
            </Typography>
            {blockers.map((b) => <Typography key={b} variant="body2">{b}</Typography>)}
          </Alert>
        )}

        {preview && preview.existingAccounts.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700} gutterBottom>
              Somebody here already has that contact detail
            </Typography>
            {preview.existingAccounts.map((a) => (
              <Typography key={a.accountId} variant="body2">
                {a.name} — {a.role}, {a.status}
              </Typography>
            ))}
          </Alert>
        )}

        <Typography variant="subtitle2" gutterBottom>What this does</Typography>
        <List dense disablePadding sx={{ mb: 2 }}>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 34 }}><BadgeIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={`Creates a ${role} account in your company`} />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 34 }}><MailIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={`Emails an invitation to ${candidate.email || 'their address'}`} />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 34 }}><DocIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={
                preview?.documentsToFile.length
                  // Named, not counted. "4 documents" tells nobody whether
                  // the right four are there.
                  ? `Files ${preview.documentsToFile.length} document(s) to their driver record`
                  : 'No documents to move — nothing was filed during hiring'
              }
              secondary={preview?.documentsToFile.map(humanKey).join(', ') || undefined}
            />
          </ListItem>
          {driver && (
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 34 }}><TrainingIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary={start === 'training'
                  ? `Starts them in training from ${firstDay}${trainerName ? `, with ${trainerName}` : ''}`
                  : 'Starts them ready to drive: no training, paid normally from day one'}
                secondary={start === 'training'
                  ? `A Training workflow runs each day for ${trainerName || 'the BCs'}: in the morning, is the trainee with them; at the end of the day, how it went and does it go on. When it is finished they set the driver's work days and capacity, and payroll pays normally from the next day. Until then every day out is a training day.`
                  : undefined}
              />
            </ListItem>
          )}
          {!!preview?.accountFields.length && (
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 34 }}><CheckIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Carries their dates onto the driver record"
                secondary={preview.accountFields.map(humanKey).join(', ')}
              />
            </ListItem>
          )}
        </List>

        <Divider sx={{ mb: 2 }} />

        <Stack gap={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              select label="Role" value={role} onChange={(e) => setRole(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField
              label="Employee ID" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
              fullWidth placeholder="Your own payroll number, if you use one"
            />
          </Stack>
          <TextField
            label={driver && start === 'training' ? 'Start date (first training day)' : 'Start date'} type="date" value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={startDate ? undefined : 'Empty is today'}
          />
          {driver && (
            <Box>
              <Typography variant="subtitle2">How they start</Typography>
              <RadioGroup value={start} onChange={(e) => setStart(e.target.value as Start)}>
                <FormControlLabel value="training" control={<Radio size="small" />} label="In training (a daily workflow until the trainer says it is finished)" />
                <FormControlLabel value="ready" control={<Radio size="small" />} label="Ready to drive, no training" />
              </RadioGroup>
              {start === 'training' && (
                <TextField
                  select label="Trainer" value={trainerId} onChange={(e) => setTrainerId(e.target.value)}
                  sx={{ minWidth: 240, mt: 1 }} helperText="Answers the daily check-in. Any BC when nobody is picked."
                >
                  <MenuItem value=""><em>Any BC</em></MenuItem>
                  {trainers.map((t) => <MenuItem key={t.accountId} value={t.accountId}>{t.name}</MenuItem>)}
                </TextField>
              )}
            </Box>
          )}
        </Stack>

      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!canHire || submitting}>
          {submitting ? 'Hiring…' : 'Hire'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HireDialog;
